import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSessionProvider } from '../../../provider/sessionContext.tsx';
import { useGetActiveEICFConsent } from '../../../entities/EICFConsents/queries/useGetActiveEICFConsent.tsx';
import { EICFConsentStatus } from '../../../entities/EICFConsents/EICFConsent.ts';
import {
  EICFConsentLockContext,
  EICFConsentLockValue,
  EICFPendingStatus,
} from './eicfConsentLockContext.ts';

interface EICFConsentGuardProps {
  children: React.ReactNode;
}

const CONSENT_PATH = '/patientDashboard/eicfConsent';
const MAIN_DASHBOARD_PATH = '/patientDashboard/mainPatient';

// Routes the patient may continue to use while a consent is pending.
// Anything outside this list redirects to the main dashboard so the patient
// sees the task list with the consent task highlighted.
const ALLOWED_PATH_PREFIXES = [
  '/login',
  '/register',
  '/resetPassword',
  '/InvitationRedirect',
  '/patientProfile',
  '/patientDashboard/profile',
  '/patientDashboard/initialProfile',
  MAIN_DASHBOARD_PATH,
  CONSENT_PATH,
];

export const EICFConsentGuard: React.FC<EICFConsentGuardProps> = observer(
  ({ children }) => {
    const { currentUser } = useSessionProvider();
    const navigate = useNavigate();
    const location = useLocation();

    const isPatient = !!currentUser?.patientID;
    const { data: activeConsent } = useGetActiveEICFConsent(isPatient);

    const pendingStatus: EICFPendingStatus =
      activeConsent &&
      (activeConsent.status === EICFConsentStatus.PendingPatientSignature ||
        activeConsent.status === EICFConsentStatus.PendingPhysicianSignature)
        ? (activeConsent.status as EICFPendingStatus)
        : null;

    const isLocked = !!(isPatient && pendingStatus);

    const onAllowedRoute = ALLOWED_PATH_PREFIXES.some((p) =>
      location.pathname.startsWith(p),
    );

    useEffect(() => {
      if (isLocked && !onAllowedRoute)
        navigate(MAIN_DASHBOARD_PATH, { replace: true });
    }, [isLocked, onAllowedRoute, navigate]);

    const value = useMemo<EICFConsentLockValue>(
      () => ({
        isLocked,
        pendingStatus,
        consentId: activeConsent?.id ?? null,
      }),
      [isLocked, pendingStatus, activeConsent?.id],
    );

    return (
      <EICFConsentLockContext.Provider value={value}>
        {children}
      </EICFConsentLockContext.Provider>
    );
  },
);

export default EICFConsentGuard;
